import React, { useState, useEffect, useCallback } from "react";
import { useAdminFetch } from "../../api/fetch";
import { useParams } from "react-router-dom";
import { TableLayout } from "../../components/layout/TableLayout";
import { GridColDef } from "@mui/x-data-grid";
import { formatDate, toStringMinutes } from "../../util/datetime";
import { Button } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useDialog } from "../../hooks/useDialog";
import { DialogTable } from "../../components/layout/DialogTable";
import { colors } from "../../theme/colors";

type Log = {
  id: number;
  lectureName: string;
  videos: Videos[];
  completedVideos: number;
}

type TableLog = UserLog & {
  log: (Log & {
    completedVideos: number;
    learningTime: string;
    lectureEndTime: string;
  })[];
}

type Videos = {
  id: number;
  testContent: string | null;
  testTime: string | null;
  videoEndTime: string | null;
  videoStartTime: string | null;
  videoWatchedSeconds: number;
  videoName: string;
  videoStatement: boolean;
}

type UserLog = {
  userName: string;
  email: string;
  log: Log[];
};

export default function UserLectureList() {
  const { userId } = useParams();
  const [userLog, setUserLog] = useState<TableLog | null>(null);
  const { get } = useAdminFetch();
  const [details, setDetails] = useState<Log | null>(null);
  const detailDialog = useDialog();

  const handleCloseDetailDialog = () => {
    setDetails(null);
    detailDialog.closeDialog();
  };
  const handleOpenDetailDialog = (log: Log) => {
    setDetails(log);
    detailDialog.openDialog();
  };

  const refresh = useCallback(async () => {
    const response = await get<{
      userName: string;
      email: string;
      log: Omit<Log, "completedVideos">[];
    }>(`/api/admin/user/` + userId);
    // きたない。。。
    setUserLog({
      userName: response.userName,
      email: response.email,
      log: response.log.map<TableLog["log"][0]>(log => {

        const completedVideos = log.videos.filter(video => video.videoStatement).length;
        const isCompleted = completedVideos === log.videos.length;
        const learningTime = log.videos.reduce((total: number, video) => total + video.videoWatchedSeconds, 0);
        const lectureEndTime = isCompleted ? formatDate(Math.max(...log.videos.filter((video: any) => video.testTime).map((video: any) => new Date(video.testTime).getTime()))) : "-";

        return {
          ...log,
          completedVideos: log.videos.filter(video => video.videoStatement).length,
          learningTime: toStringMinutes(learningTime),
          lectureEndTime,
        }
      })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!userLog) {
    return <div>loading...</div>;
  }

  return (
    <>
      <TableLayout
        header={{
          title: `ユーザー名: ${userLog.userName}（メールアドレス：${userLog.email}）`
        }}
        table={{
          data: userLog.log,
          columns: buildColumns(handleOpenDetailDialog),
          getRowKey: (row) => row.id,
        }}
      />
      <DialogTable
        open={detailDialog.isOpen}
        onClose={handleCloseDetailDialog}
        title="講座詳細"
        table={{
          data: details ? details.videos : [],
          columns: dialogTableColumns,
          getRowKey: (row) => row.id,
        }}
      />

    </>
  );
}

const buildColumns = (handleOpenDetailDialog: (log: Log) => void): GridColDef<TableLog["log"][0]>[] => [
  {
    field: "lectureName",
    headerName: "講座名",
    align: "center",
    flex: 3,
    renderCell: (param) => (
      <>{param.row.lectureName}</>
    ),
  },
  {
    field: "status",
    headerName: "ステータス",
    align: "center",
    flex: 1,
    renderCell: (param) => {
      const totalVideos = param.row.videos.length;
      if (totalVideos === 0) {
        return <>動画なし</>;
      }
      if (param.row.completedVideos === totalVideos) {
        return <>完了</>;
      }
      return (
        <>未完了</>
      );
    },
  },
  {
    field: "endDate",
    headerName: "修了日",
    align: "center",
    flex: 1,
    renderCell: (param) => <>{param.row.lectureEndTime}</>,
  },
  {
    field: "learningTime",
    headerName: "学習時間",
    align: "center",
    flex: 1,
    renderCell: (param) => <>{param.row.learningTime}</>,
  },
  {
    field: "watchStatus",
    headerName: "動画視聴状況",
    align: "center",
    flex: 1,
    renderCell: (param) => {
      const totalVideos = param.row.videos.length;
      if (totalVideos === 0) {
        return <>動画なし</>;
      }
      const completedVideos = param.row.videos.filter(video => video.videoStatement).length;
      const percentage = Math.round((completedVideos / totalVideos) * 100);
      return (
        <> {percentage}% </>
      );
    },
  },
  {
    field: "testStatus",
    headerName: "小テスト回答状況",
    align: "center",
    flex: 1,
    renderCell: (param) => {
      const totalVideos = param.row.videos.length;
      if (totalVideos === 0) {
        return <>動画なし</>;
      }
      const completedTests = param.row.videos.filter(video => video.testTime !== null).length;
      const percentage = Math.round((completedTests / totalVideos) * 100);
      return (
        <> {percentage}% </>
      );
    },
  },
  {
    field: "details",
    headerName: "詳細",
    align: "center",
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (param) => (
      <Button
        variant="contained"
        onClick={() => handleOpenDetailDialog(param.row)}
        sx={{
          borderRadius: "20px",
          backgroundColor: colors.accent,
          "&:hover": {
            backgroundColor: colors.accent,
          },
        }}
      >
        <AccessTimeIcon />
      </Button>
    ),
  },
];

export const dialogTableColumns: GridColDef<Videos>[] = [
  {
    field: "videoName",
    headerName: "動画名",
    align: "center",
    flex: 3,
    renderCell: (param) => (
      <>{param.row.videoName}</>
    )
  },
  {
    field: "watchStatus",
    headerName: "視聴状況",
    align: "center",
    flex: 1,
    renderCell: (param) => (
      <>{param.row.videoEndTime ? <>視聴済み</> : <>-</>}</>
    )
  },
  {
    field: "testContent",
    headerName: "小テスト回答",
    align: "center",
    flex: 10,
    renderCell: (param) => (
      <>{param.row.testContent || "-"}</>
    )
  },
  {
    field: "testTime",
    headerName: "テスト回答時間",
    align: "center",
    flex: 2,
    renderCell: (param) => {
      if (!param.row.testTime) {
        return <>未回答</>;
      }
      return <>{formatDate(param.row.testTime, true)}</>;
    }
  }
]